import { useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { CostCenterByLegalEntitiesDocument } from 'generated/resources/graphql'
import { debounce } from 'lodash'

import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'

export interface CostCenterOption {
  id: string
  name: string
  workplaceId?: number | null
  jobCatalogNodeUuid?: string | null
  jobCatalogFamilyName?: string | null
  jobCatalogFunctionName?: string | null
  jobCatalogRoleName?: string | null
}

interface Props {
  legalEntityIds: number[]
  excludeCostCenterIds: number[]
  onlyJobCatalog?: boolean
  onlyWorkplace?: boolean
}

export const useCostCenterByLegalEntities = ({
  excludeCostCenterIds,
  legalEntityIds,
  onlyJobCatalog,
  onlyWorkplace,
}: Props) => {
  const [search, setSearch] = useState('')
  const debouncedOnSearch = useMemo(
    () =>
      debounce((val: string) => {
        setSearch(val)
      }, 200),
    []
  )

  const clearSearch = useMemo(
    () => () => {
      setSearch('')
    },
    []
  )

  const { hasNewStructureEnabled } = useOutletContext<{
    hasNewStructureEnabled: boolean
  }>()

  const { nodes, controls, loading } = usePaginatedQuery(CostCenterByLegalEntitiesDocument, {
    variables: {
      legalEntityIds,
      search,
      sourceType: hasNewStructureEnabled ? 'structure_based' : 'legacy',
      onlyJobCatalog: onlyJobCatalog ?? undefined,
      onlyWorkplace: onlyWorkplace ?? undefined,
    },
    extractConnection: (data) => data.costCenters.costCentersConnection,
  })

  const costCenterOptions: CostCenterOption[] = nodes.map(
    (node): CostCenterOption => ({
      id: `${node.id}`,
      name: `${node.code ? `${node.code} - ${node.name}` : node.name}`,
      jobCatalogFamilyName: node.jobCatalogFamilyName,
      jobCatalogFunctionName: node.jobCatalogFunctionName,
      jobCatalogRoleName: node.jobCatalogRoleName,
      workplaceId: node.workplaceId,
      jobCatalogNodeUuid: node.jobCatalogNodeUuid,
    })
  )

  // Legacy mode: filter by excludeCostCenterIds
  const filteredCostCenterOptions = costCenterOptions.filter(
    (op) => !excludeCostCenterIds.includes(Number(op.id))
  )

  // Structured mode: job structure options (where jobCatalogNodeUuid is not null)
  const jobStructureOptions = costCenterOptions.filter((op) => op.jobCatalogNodeUuid != null)

  // Structured mode: workplace options (where workplaceId is not null)
  const workplaceOptions = costCenterOptions.filter((op) => op.workplaceId != null)

  return {
    controls,
    rawCostCenterOptions: costCenterOptions,
    debouncedOnSearch,
    clearSearch,
    filteredCostCenterOptions,
    // Structured mode options
    jobStructureOptions,
    workplaceOptions,
    hasNewStructureEnabled,
    loading,
  }
}
